import { useQuery } from 'react-query';
import { sellerApiClient } from '@api/apiClient/sellerApiClient';

export interface TRegion {
  country: string;
  id: string;
  nameCn: string;
  isMunicipality: boolean;
}

export interface TDistrict {
  id: string;
  nameCn: string;
}

export interface TCitySuggestion {
  adcode: string;
  nameCn: string;
}

export interface TRegionResponse {
  provinces: TRegion[];
}

export interface TAddress {
  title: string,
  address: string,
  location: {
    lat: number,
    lng: number,
  },
  adcode: string,
  id: string,
  category: string,
  categoryCode: number,
  type: number,
  distance: number,
  province: string,
  city: string,
  district: string,
  subPois: [
    {
      parentId: string,
      id: string,
      title: string,
      address: string,
      category: string,
      location: {
        lat: number,
        lng: number,
      },
      adcode: number,
      city: string,
    }
  ],
}

export interface TAddressResultCN {
  data: TAddress[];
}

export interface TSuggestionTR {
  fullAddress: string,
  city: string,
  street: string,
  houseNumber: string,
  apartment: string,
  postalCode: string,
}

export interface TAddressResultTR {
  data: TSuggestionTR[];
}

export interface TSuggestionTRByIndex {
  postalCode: string,
  region: string,
  city: string,
}

export interface TAddressResultTRByIndex {
  data: TSuggestionTRByIndex[];
}

export  interface TValidationInfo {
  message: string;
  result: {
    title: string,
    location: {
      lat: number,
      lng: number,
    },
    adInfo: {
      province: string,
      city: string,
      district: string,
      adcode: string,
    },
    addressComponents: {
      province: string,
      city: string,
      district: string,
      street: string,
      streetNumber: string,
    },
    similarity: number,
    deviation: number,
    reliability: number,
    level: number,
  }
}

export const getRegions = async (): Promise<TRegionResponse> => {
  const response = await sellerApiClient.get<TRegionResponse>('/v1/map/provinces');

  return response.data;
};

export const getSuggestionsCn = async (params: {
  provinceId: string,
  keyword: string,
  parentRegion?: string,
}): Promise<TAddressResultCN> => {
  const response = await sellerApiClient.get<TAddressResultCN>(
    '/v1/map/cn-suggestions',
    { params },
  );

  return response.data;
};

export const getSuggestionsCnCity = async (params: {
  provinceId: string,
  keyword: string,
  cityName?: string,
}): Promise<TCitySuggestion[]> => {
  const response = await sellerApiClient.get<{cnCityAndDistricts: TCitySuggestion[]}>(
    '/v1/map/cn-city-and-districts',
    { params },
  );

  return response.data?.cnCityAndDistricts || [];
};

export const getSuggestionsTrByIndex = async (params: {
  postalCode: string,
}): Promise<TAddressResultTRByIndex> => {
  const response = await sellerApiClient.get<TAddressResultTRByIndex>(
    '/v1/map/postalcode-info',
    { params },
  );

  return response.data;
};

export const getSuggestionsTr = async (params: {
  region: string,
  city: string,
  keyword: string,
}): Promise<TAddressResultTR> => {
  const response = await sellerApiClient.get<TAddressResultTR>(
    '/v1/map/tr-suggestions',
    { params },
  );

  return response.data;
};

export interface TDistrictResult {
  districts: TDistrict[];
}

export const getDistrictSuggestions = async (params: {
  countryCode: string,
  keyword: string,
}): Promise<TDistrictResult> => {
  const response = await sellerApiClient.get<TDistrictResult>(
    '/v1/map/districts',
    { params },
  );

  return response.data;
};

export function useDistrictSuggestions(
  countryCode: string,
  keywordRaw: string,
) {
  const keyword = keywordRaw.trim();
  const { data, refetch, isLoading, isFetching, isFetched, isError } = useQuery({
    queryKey: ["getDistrictSuggestions", countryCode, keyword],
    queryFn: () => getDistrictSuggestions({ countryCode, keyword }),
    retry: 1,
    select: (data: TDistrictResult) => (data.districts || []),
    staleTime: Infinity,
  });

  return {
    isLoading,
    isFetching,
    isFetched,
    isError,
    data,
    refetch,
  };
}

export function useAddressSuggestionsCn(
  provinceId: string,
  parentRegion: string,
  keywordRaw: string,
) {
  const keyword = keywordRaw.trim();
  const { data, refetch, isLoading, isFetching, isFetched, isError } = useQuery({
    queryKey: ["getAddressSuggestions-cn", provinceId, parentRegion, keyword],
    queryFn: () => getSuggestionsCn({ provinceId, keyword, parentRegion }),
    retry: 1,
    enabled: Boolean(provinceId && keyword),
    select: (data: TAddressResultCN) => (data.data || []),
    staleTime: Infinity,
  });

  return {
    isLoading,
    isFetching,
    isFetched,
    isError,
    data,
    refetch,
  };
}

export function useAddressSuggestionsCnCity(
  provinceId: string,
  keywordRaw: string,
) {
  const keyword = keywordRaw.trim();
  const { data, refetch, isLoading, isFetching, isFetched, isError } = useQuery({
    queryKey: ["getAddressSuggestions-cn-city", provinceId, keyword],
    queryFn: () => getSuggestionsCnCity({ provinceId, keyword }),
    retry: 1,
    enabled: Boolean(provinceId && keyword),
    // select: (data: TAddressResultCN) => (data.data || []),
    staleTime: Infinity,
  });

  return {
    isLoading,
    isFetching,
    isFetched,
    isError,
    data,
    refetch,
  };
}

export function useAddressSuggestionsCnCityDistricts(
  provinceId: string,
  cityName: string,
  keywordRaw: string,
) {
  const keyword = keywordRaw.trim();
  const { data, refetch, isLoading, isFetching, isFetched, isError } = useQuery({
    queryKey: ["getAddressSuggestions-cn-city-districts", provinceId, cityName, keyword],
    queryFn: () => getSuggestionsCnCity({ provinceId, keyword, cityName }),
    retry: 1,
    enabled: Boolean(provinceId && keyword),
    // select: (data: TAddressResultCN) => (data.data || []),
    staleTime: Infinity,
  });

  return {
    isLoading,
    isFetching,
    isFetched,
    isError,
    data,
    refetch,
  };
}

export function useAddressSuggestionsTr({ region, city, keyword: keywordRaw }: {
  region: string,
  city: string,
  keyword: string,
}) {
  const keyword = keywordRaw.trim();
  const { data, refetch, isLoading, isFetching, isFetched, isError } = useQuery({
    queryKey: ["getAddressSuggestions-tr", region, city, keyword],
    queryFn: () => getSuggestionsTr({ keyword, city, region }),
    retry: 1,
    enabled: Boolean(keyword),
    select: (data: TAddressResultTR) => (data.data || []),
    staleTime: Infinity,
  });

  return {
    isLoading,
    isFetching,
    isFetched,
    isError,
    data,
    refetch,
  };
}

export function useAddressSuggestionsTrByIndex(postalCode: string) {
  const postalCodeTrimmed = postalCode.trim();
  const { data, refetch, isLoading, isFetching, isFetched, isError } = useQuery({
    queryKey: ["getAddressSuggestions-tr-by-index", postalCodeTrimmed],
    queryFn: () => getSuggestionsTrByIndex({ postalCode: postalCodeTrimmed }),
    retry: 1,
    enabled: Boolean(postalCodeTrimmed),
    select: (data: TAddressResultTRByIndex) => (data.data || []),
    staleTime: Infinity,
  });

  return {
    isLoading,
    isFetching,
    isFetched,
    isError,
    data,
    refetch,
  };
}

export const getValidation = async ({ address }: {
  address: string,
}): Promise<TValidationInfo> => {
  const response = await sellerApiClient.get<TValidationInfo>(
    '/v1/map/valid-address',
    { params: { address } },
  );

  return response.data;
};
